import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Check, X as XIcon, Trash2, UserPlus, Info, RefreshCw } from "lucide-react";
import { fmtDate, fmtTimeRange } from "@/lib/format";
import TimeInput12 from "@/components/TimeInput12";
import DateInputMDY from "@/components/DateInputMDY";
const STATUS_STYLES = {
  pending: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  approved: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  rejected: "text-red-400 border-red-500/30 bg-red-500/10",
};

// Special value for the "myself" option in the target-user dropdown — Select
// primitives can't take an empty-string value.
const SELF_TARGET = "__self__";

export default function Conflicts() {
  const { user } = useAuth();
  const isStaff = ["org_admin", "production_staff", "super_admin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [castMembers, setCastMembers] = useState([]);
  const [open, setOpen] = useState(false);
  // Iter 78 — Pending / Approved tabs. Pending is the default landing tab
  // so unreviewed items always land in front of the reviewer. Rejected
  // items fold into Approved-tab-adjacent "Resolved" so the reviewer only
  // has two lists to think about (needs action / already handled).
  const [tab, setTab] = useState("pending");
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    start_time: "18:00",
    end_time: "22:00",
    reason: "",
    target_user_id: SELF_TARGET,   // staff can flip this to a cast member
  });

  const load = () => api.get("/conflicts").then((r) => setItems(r.data));
  const loadCast = async () => {
    // Only staff need the target dropdown — fetching members for cast is
    // wasted bandwidth (and their production_id may not resolve to a
    // members list they can read).
    if (!isStaff) return;
    try {
      const { data } = await api.get("/users");
      // /users returns members of the current production. Filter to cast so
      // staff can only assign blackouts to actual cast members.
      setCastMembers((data || []).filter((u) => u.role === "cast"));
    } catch { /* silent — dropdown just stays empty */ }
  };
  useEffect(() => { load(); loadCast(); }, []);

  const targetLabel = useMemo(() => {
    if (!isStaff || form.target_user_id === SELF_TARGET) return "yourself";
    const t = castMembers.find((c) => c.id === form.target_user_id);
    return t ? t.name : "the selected cast member";
  }, [isStaff, form.target_user_id, castMembers]);

  const submit = async () => {
    if (!form.reason.trim()) return toast.error("Please describe the reason");
    try {
      const payload = { ...form };
      // Only send target_user_id when staff picked someone else.
      if (payload.target_user_id === SELF_TARGET) delete payload.target_user_id;
      await api.post("/conflicts", payload);
      toast.success(payload.target_user_id
        ? `Conflict added for ${targetLabel}`
        : "Conflict submitted");
      setOpen(false);
      setForm({ ...form, reason: "", target_user_id: SELF_TARGET });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit conflict"); }
  };
  const review = async (id, status) => {
    await api.patch(`/conflicts/${id}`, { status });
    toast.success(`Marked ${status}`);
    load();
    // After approving, hop the reviewer to the Approved tab so they see
    // the item they just resolved — but only if that was the last pending.
    if (status === "approved") {
      const remaining = items.filter((c) => c.status === "pending" && c.id !== id);
      if (remaining.length === 0) setTab("approved");
    }
  };
  const remove = async (id) => {
    await api.delete(`/conflicts/${id}`);
    load();
  };

  // Iter 78 — split rows by tab. Pending items always show first (their
  // own tab); Approved tab bundles approved + rejected so "already dealt
  // with" is one place. Both tabs sort by date ascending.
  const pending = useMemo(
    () => items.filter((c) => c.status === "pending"),
    [items],
  );
  const resolved = useMemo(
    () => items.filter((c) => c.status !== "pending"),
    [items],
  );
  const visibleItems = tab === "pending" ? pending : resolved;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="conflicts-page">
      {/* Iter 72 reminder — cast-submitted conflicts don't gate the
          scheduler until a staff approves them; makes the review workflow
          obvious so directors know why an unapproved conflict didn't block
          a rehearsal. */}
      <div
        data-testid="conflicts-approval-banner"
        className="rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 px-4 py-3 flex items-start gap-3 text-sm"
      >
        <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" />
        <div>
          <div className="font-medium text-amber-100">Approval required</div>
          <div className="text-[12px] leading-relaxed text-amber-200/80">
            Conflicts stay hidden from the rehearsal scheduler until a production_staff member approves them.
            Staff-entered blackouts are auto-approved and take effect immediately.
          </div>
        </div>
      </div>
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Availability</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Conflict Submissions</h1>
          <p className="mt-1 text-zinc-400 text-sm">
            {isStaff
              ? "Log blackout times for cast members or yourself. Staff-entered conflicts are auto-approved."
              : "Report scheduling conflicts; production will review."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isStaff && (
            <Button
              data-testid="sync-audition-conflicts-btn"
              variant="outline"
              className="border-white/10 text-zinc-200 hover:bg-white/5"
              onClick={async () => {
                try {
                  const { data } = await api.post("/auditions/sync-conflicts");
                  if (data.conflicts_added > 0) {
                    toast.success(`Imported ${data.conflicts_added} conflict${data.conflicts_added === 1 ? "" : "s"} from audition sign-ups.`);
                    load();
                  } else {
                    toast.info("No new audition conflicts to import — everyone's up to date.");
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not sync audition conflicts");
                }
              }}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Pull from auditions
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-conflict-btn" className="bg-gold text-black hover:bg-gold-hover">
                <Plus className="w-4 h-4 mr-1" />
                {isStaff ? "Add conflict" : "Submit conflict"}
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">New conflict</DialogTitle></DialogHeader>
            {isStaff && (
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-400 inline-flex items-center gap-1">
                  <UserPlus className="w-3 h-3" /> For which cast member?
                </Label>
                <Select
                  value={form.target_user_id}
                  onValueChange={(v) => setForm({ ...form, target_user_id: v })}
                >
                  <SelectTrigger data-testid="conflict-target-select" className="bg-[#0A0A0A] border-white/10 mt-1">
                    <SelectValue placeholder="Select a cast member…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10 text-zinc-200 max-h-72">
                    <SelectItem value={SELF_TARGET} data-testid="conflict-target-self">
                      Myself ({user.name})
                    </SelectItem>
                    {castMembers.length === 0 && (
                      <div className="px-2 py-1.5 text-[11px] text-zinc-500">No cast members loaded.</div>
                    )}
                    {castMembers.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`conflict-target-${c.id}`}>
                        {c.name}{c.character ? ` · ${c.character}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-zinc-500 mt-1">
                  {form.target_user_id === SELF_TARGET
                    ? "This will submit a conflict for your own calendar."
                    : `Auto-approved and filed under ${targetLabel}. They'll see it in their conflict list.`}
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Date</Label><DateInputMDY value={form.date} onChange={(v) => setForm({ ...form, date: v })} /></div>
              <div><Label>Start</Label><TimeInput12 value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} /></div>
              <div><Label>End</Label><TimeInput12 value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} /></div>
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea data-testid="conflict-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="bg-[#0A0A0A] border-white/10" />
            </div>
            <DialogFooter>
              <Button data-testid="submit-conflict-btn" onClick={submit} className="bg-gold text-black hover:bg-gold-hover">
                {isStaff && form.target_user_id !== SELF_TARGET ? "Add for cast member" : "Submit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <div className="flex items-center gap-1 border-b border-white/10" data-testid="conflicts-tabs">
        <button
          data-testid="conflicts-tab-pending"
          onClick={() => setTab("pending")}
          className={`relative px-4 py-2 text-sm inline-flex items-center gap-2 transition-colors ${
            tab === "pending" ? "text-gold" : "text-zinc-400 hover:text-white"
          }`}
        >
          Pending
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
            pending.length > 0 ? "bg-amber-500/20 text-amber-200 border border-amber-500/30" : "bg-white/5 text-zinc-500"
          }`}>{pending.length}</span>
          {tab === "pending" && <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-gold" />}
        </button>
        <button
          data-testid="conflicts-tab-approved"
          onClick={() => setTab("approved")}
          className={`relative px-4 py-2 text-sm inline-flex items-center gap-2 transition-colors ${
            tab === "approved" ? "text-gold" : "text-zinc-400 hover:text-white"
          }`}
        >
          Approved
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-500 border border-white/10">{resolved.length}</span>
          {tab === "approved" && <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-gold" />}
        </button>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#121212] divide-y divide-white/5">
        {visibleItems.length === 0 && (
          <div className="p-6 text-sm text-zinc-500" data-testid={`conflicts-empty-${tab}`}>
            {tab === "pending"
              ? "No conflicts waiting on review — you're all caught up."
              : "No approved or rejected conflicts yet."}
          </div>
        )}
        {visibleItems.map((c) => (
          <div key={c.id} data-testid={`conflict-row-${c.id}`} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
            <div className="md:w-56">
              <div className="text-sm font-medium">{c.user_name}</div>
              <div className="text-xs text-zinc-500">{fmtDate(c.date)} · {fmtTimeRange(c.start_time, c.end_time)}</div>
              {c.entered_by_name && (
                <div className="text-[10px] text-zinc-500 mt-1 italic" data-testid={`conflict-entered-by-${c.id}`}>
                  Entered by {c.entered_by_name}
                </div>
              )}
            </div>
            <div className="flex-1 text-sm text-zinc-300">{c.reason}</div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${STATUS_STYLES[c.status] || ""}`}>{c.status}</span>
            <div className="flex items-center gap-2">
              {isStaff && c.status === "pending" && (
                <>
                  <button data-testid={`approve-${c.id}`} onClick={() => review(c.id, "approved")} className="p-1.5 rounded border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"><Check className="w-4 h-4" /></button>
                  <button data-testid={`reject-${c.id}`} onClick={() => review(c.id, "rejected")} className="p-1.5 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10"><XIcon className="w-4 h-4" /></button>
                </>
              )}
              {/* Iter 78 — allow staff to move a resolved item back to
                  Pending in case they clicked wrong or a situation changes. */}
              {isStaff && c.status !== "pending" && (
                <button
                  data-testid={`reopen-${c.id}`}
                  onClick={() => review(c.id, "pending")}
                  title="Move back to Pending"
                  className="p-1.5 rounded border border-white/10 text-zinc-400 hover:text-amber-300 hover:border-amber-500/40"
                >
                  <Info className="w-4 h-4" />
                </button>
              )}
              {(isStaff || c.user_id === user.id) && (
                <button data-testid={`delete-${c.id}`} onClick={() => remove(c.id)} className="p-1.5 rounded border border-white/10 text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
